import { OPERATIONAL_EVENT_TYPE } from '@atlaskit/analytics-gas-types';

import { FORGE_UI_ANALYTICS_CHANNEL } from '../../analytics';
import {
	on as subscribeToEvent,
	emit as dispatchEvent,
	type Subscription,
} from '../../custom-ui/bridge/methods/events';
import { createBridgeResource, type BridgeResource } from '../bridge-resource';
import type { MakeBridgeContext } from '../types';
import type { TBridgeMethod } from './types';

// ─── Resource ─────────────────────────────────────────────────────────────────

type EventsState = {
	subscriptions: Set<Subscription>;
};

/** @visibleForTesting */
export const eventsResource: BridgeResource<EventsState> = createBridgeResource<EventsState>(
	'events',
	() => ({ subscriptions: new Set() }),
	({ subscriptions }) => {
		for (const sub of subscriptions) {
			sub.unsubscribe();
		}
		subscriptions.clear();
	},
);

// ─── Context slices ───────────────────────────────────────────────────────────

type OnContext = MakeBridgeContext<
	'extension' | 'extensionDetails' | 'claimBridgeResource' | 'page',
	'analytics'
>;
type EmitContext = MakeBridgeContext<'extensionDetails'>;

// ─── Payload / result shapes ──────────────────────────────────────────────────

type EventCallbackPayload = {
	callback: (payload?: any) => Promise<any>;
	event: string;
};

type EmitPayload = {
	event: string;
	payload?: any;
};

export type SubscriptionHandle = {
	unsubscribe: () => void;
};

// ─── Bridge methods ───────────────────────────────────────────────────────────

/**
 * Subscribes to a forge.bridge DOM event scoped to the current extension.
 * On component unmount, all active subscriptions are closed via `eventsResource.destroy`.
 */
export const on: TBridgeMethod<OnContext, EventCallbackPayload, SubscriptionHandle> = async (
	{ extensionDetails, claimBridgeResource, page, services: { analytics } },
	{ event, callback },
) => {
	const state = claimBridgeResource(eventsResource);
	const subscription = subscribeToEvent(event, callback, extensionDetails);
	state.subscriptions.add(subscription);

	analytics
		.createAnalyticsEvent({
			eventType: OPERATIONAL_EVENT_TYPE,
			data: {
				action: 'subscribed',
				actionSubject: 'bridgeEvent',
				attributes: {
					// TODO: remove the condition (and related test) once Developer UGC spec is ready
					event: event === 'JIRA_ISSUE_CHANGED' ? 'JIRA_ISSUE_CHANGED' : 'other',
				},
				source: page,
				tags: ['forge'],
			},
		})
		.fire(FORGE_UI_ANALYTICS_CHANNEL);

	return {
		unsubscribe: () => {
			if (state.subscriptions.delete(subscription)) {
				subscription.unsubscribe();
			}
		},
	};
};

/**
 * Dispatches a forge.bridge event
 */
export const emit: TBridgeMethod<EmitContext, EmitPayload, void> = async (
	{ extensionDetails },
	{ event, payload },
) => {
	dispatchEvent(event, payload, extensionDetails);
};
